import { Dropdown, IColumn, IDropdownOption, MessageBar, MessageBarType, SelectionMode, ShimmeredDetailsList, Stack } from "@fluentui/react"
import { useCallback, useEffect, useState } from "react"
import { safeFormatDateValue } from "../lib/helper"
import { makeACMGCRMApiCall } from "../services/apiService"
import { IAccount } from "../types/IAccount"
import { IOpportunity } from "../types/IOpportunity"
import { StickeyHeaderDetailsList } from "./Shared/StickeyHeaderDetailsList"

interface OpportunityHistoryProps {
	group: IAccount
}

export function OpportunityHistory({ group }: OpportunityHistoryProps) {
	const [loading, setLoading] = useState<boolean>(true)
	const [error, setError] = useState<string>()
	const [opportunities, setOpportunities] = useState<IOpportunity[]>([])
	const [displayItems, setDisplayItems] = useState<IOpportunity[]>([])
	const [selectedStatus, setSelectedStatus] = useState<string>("All")

	const sortByDate = (items: IOpportunity[], isDescending: boolean) => {
		return [...items].sort((a, b) => {
			const dateA = a.cradf_effectivedate ? new Date(a.cradf_effectivedate).getTime() : 0
			const dateB = b.cradf_effectivedate ? new Date(b.cradf_effectivedate).getTime() : 0
			return isDescending ? dateB - dateA : dateA - dateB
		})
	}

	const getOpportunities = useCallback(async (): Promise<IOpportunity[]> => {
		const apiUrl = `getOpportunities?groupId=${group.accountid}`
		try {
			const results = await makeACMGCRMApiCall(apiUrl)
			return results as IOpportunity[]
		} catch (error) {
			console.error("Error fetching opportunities", error)
			throw error
		}
	}, [group.accountid])

	useEffect(() => {
		const fetchData = async () => {
			try {
				if (group) {
					const results = await getOpportunities()
					const sorted = sortByDate(results, true)
					setOpportunities(results)
					setDisplayItems(sorted)
				}
			} catch (err) {
				console.log(err)
				setError("Error getting opportunities")
			} finally {
				setLoading(false)
			}
		}
		fetchData()
	}, [group, getOpportunities])

	const onStatusFilterChange = (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
		const status = option?.key as string
		setSelectedStatus(status)

		if (status === "All") {
			setDisplayItems(sortByDate(opportunities, true))
		} else {
			const filtered = opportunities.filter(o => o.statecode === status)
			setDisplayItems(sortByDate(filtered, true))
		}
	}

	const getColumns = () => {
		const retVal: IColumn[] = [
			{
				key: "statecode",
				name: "Status",
				fieldName: "statecode",
				minWidth: 70,
				maxWidth: 100,
				isResizable: true,
				onRender: (item: IOpportunity) => {
					return item.statecode === "0" ? "Open" : item.statecode === "1" ? "Won" : "Lost"
				}
			},
			{
				key: "cradf_effectivedate",
				name: "Effective Date",
				fieldName: "cradf_effectivedate",
				minWidth: 100,
				maxWidth: 120,
				isResizable: true,
				onRender: (item: IOpportunity) => {
					return safeFormatDateValue(item.cradf_effectivedate, "MM/dd/yyyy")
				}
			},
			{
				key: "cw_opportunitystage",
				name: "Opportunity Stage",
				fieldName: "cw_opportunitystage",
				minWidth: 150,
				maxWidth: 200,
				isResizable: true,
				onRender: (item: IOpportunity) => {
					return item.cw_opportunitystage || "N/A"
				}
			},
			{
				key: "cw_preppedquotedocs",
				name: "Quote Docs Prepared",
				fieldName: "cw_preppedquotedocs",
				minWidth: 100,
				maxWidth: 120,
				isResizable: true,
				onRender: (item: IOpportunity) => {
					return safeFormatDateValue(item.cw_preppedquotedocs, "MM/dd/yyyy", "")
				}
			},
			{
				key: "cw_sentplanoptions",
				name: "Quote Sent",
				fieldName: "cw_sentplanoptions",
				minWidth: 100,
				maxWidth: 120,
				isResizable: true,
				onRender: (item: IOpportunity) => {
					return safeFormatDateValue(item.cw_sentplanoptions, "MM/dd/yyyy", "")
				}
			},
			{
				key: "cw_receivedplanoptions",
				name: "Quote Signed",
				fieldName: "cw_receivedplanoptions",
				minWidth: 100,
				maxWidth: 120,
				isResizable: true,
				onRender: (item: IOpportunity) => {
					return safeFormatDateValue(item.cw_receivedplanoptions, "MM/dd/yyyy", "")
				}
			},
			{
				key: "cw_preppedcontractdocs",
				name: "Contract Docs Prepared",
				fieldName: "cw_preppedcontractdocs",
				minWidth: 100,
				maxWidth: 120,
				isResizable: true,
				onRender: (item: IOpportunity) => {
					return safeFormatDateValue(item.cw_preppedcontractdocs, "MM/dd/yyyy", "")
				}
			},
			{
				key: "cw_sentdocstogroupforesignature",
				name: "Contract Sent",
				fieldName: "cw_sentdocstogroupforesignature",
				minWidth: 100,
				maxWidth: 120,
				isResizable: true,
				onRender: (item: IOpportunity) => {
					return safeFormatDateValue(item.cw_sentdocstogroupforesignature, "MM/dd/yyyy", "")
				}
			},
			{
				key: "cw_docsesigned",
				name: "Contract Signed",
				fieldName: "cw_docsesigned",
				minWidth: 100,
				maxWidth: 120,
				isResizable: true,
				onRender: (item: IOpportunity) => {
					return safeFormatDateValue(item.cw_docsesigned, "MM/dd/yyyy", "")
				}
			}
		]
		return retVal
	}

	const statusOptions: IDropdownOption[] = [
		{ key: "All", text: "All Statuses" },
		{ key: "0", text: "Open" },
		{ key: "1", text: "Won" },
		{ key: "2", text: "Lost" }
	]

	return (
		<>
			<Stack tokens={{ childrenGap: 10 }} style={{ padding: 15 }}>
				{error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}
				{!loading && (
					<Stack horizontal verticalAlign="end" tokens={{ childrenGap: 20 }}>
						<Dropdown label="Filter by Status" selectedKey={selectedStatus} options={statusOptions} onChange={onStatusFilterChange} styles={{ dropdown: { width: 200 } }} />
					</Stack>
				)}
				{loading && <ShimmeredDetailsList items={[]} columns={getColumns()} enableShimmer />}
				{!loading && !(opportunities?.length === 0) && (
					<Stack tokens={{ childrenGap: 10 }}>
						<StickeyHeaderDetailsList items={displayItems} columns={getColumns()} selectionMode={SelectionMode.none} />
					</Stack>
				)}
			</Stack>
		</>
	)
}
